'use client';

import { BarChart3, Bell, Calendar, DollarSign, ShieldCheck, Users } from 'lucide-react';
import Link from 'next/link';

const features = [
  {
    icon: Calendar,
    title: 'Agendamento Inteligente',
    description:
      'Sistema completo de agendamento com calendário integrado, lembretes automáticos e gestão de horários.',
    color: 'var(--sisce-cyan)',
  },
  {
    icon: Users,
    title: 'Gestão de Clientes',
    description:
      'Cadastro completo de clientes, histórico de atendimentos e fichas personalizadas de avaliação.',
    color: 'var(--sisce-pink)',
  },
  {
    icon: DollarSign,
    title: 'Controle Financeiro',
    description:
      'Acompanhamento de receitas, despesas, comissões e relatórios financeiros detalhados.',
    color: 'var(--sisce-orange)',
  },
  {
    icon: BarChart3,
    title: 'Relatórios e Análises',
    description:
      'Dashboard com métricas em tempo real, gráficos de desempenho e análise de resultados.',
    color: 'var(--sisce-yellow)',
  },
  {
    icon: Bell,
    title: 'Notificações e Lembretes',
    description:
      'Sistema automatizado de lembretes por SMS, WhatsApp e e-mail para clientes e profissionais.',
    color: 'var(--sisce-purple)',
  },
  {
    icon: ShieldCheck,
    title: 'Segurança e Privacidade',
    description: 'Proteção de dados com criptografia, backup automático e conformidade com LGPD.',
    color: 'var(--sisce-navy)',
  },
];

export default function Home() {
  return (
    <>
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative bg-linear-to-br from-white via-purple-50/30 to-pink-50/30 py-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-5xl mb-6 bg-linear-to-r from-(--sisce-cyan) via-(--sisce-pink) to-(--sisce-purple) bg-clip-text text-transparent">
                Sistema inteligente para Clínica de Estética
              </h1>
              <p className="text-xl text-foreground/70 mb-8">
                Transforme a gestão do seu negócio de estética com tecnologia de ponta. Agendamento,
                controle financeiro e muito mais em uma única plataforma.
              </p>
              <div className="flex gap-4 justify-center">
                <Link
                  href="/funcionalidades"
                  className="bg-linear-to-r from-(--sisce-cyan) to-(--sisce-pink) text-white px-8 py-4 rounded-lg hover:opacity-90 transition-opacity"
                >
                  Ver Todas as Funcionalidades
                </Link>
                <a
                  href="#contato"
                  className="border-2 border-(--sisce-navy) text-(--sisce-navy) px-8 py-4 rounded-lg hover:bg-(--sisce-navy) hover:text-white transition-colors"
                >
                  Solicitar Demonstração
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-10 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl mb-4">Funcionalidades Principais</h2>
              <p className="text-xl text-foreground/70">
                Tudo que você precisa para gerenciar seu negócio com excelência
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className="group p-6 rounded-xl border border-border hover:shadow-xl transition-all duration-300 bg-white hover:scale-105"
                  >
                    <div
                      className="w-14 h-14 rounded-lg flex items-center justify-center mb-4"
                      style={{ backgroundColor: `${feature.color}20` }}
                    >
                      <Icon className="w-7 h-7" style={{ color: feature.color }} />
                    </div>
                    <h3 className="text-xl mb-3">{feature.title}</h3>
                    <p className="text-foreground/70">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-10 bg-linear-to-br from-purple-50/30 to-pink-50/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl mb-4">Por que escolher o SISCE?</h2>

              <p className="text-xl text-foreground/70">
                Tecnologia desenvolvida para clínicas modernas
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-black/5">
                <div className="text-5xl mb-6">⚡</div>

                <h3 className="text-2xl mb-4">Automação Inteligente</h3>

                <p className="text-foreground/70">
                  Automatize confirmações, lembretes e comunicação via WhatsApp.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm border border-black/5">
                <div className="text-5xl mb-6">📊</div>

                <h3 className="text-2xl mb-4">Gestão Completa</h3>

                <p className="text-foreground/70">
                  Controle financeiro, agenda, clientes e relatórios em um só lugar.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm border border-black/5">
                <div className="text-5xl mb-6">🔒</div>

                <h3 className="text-2xl mb-4">Segurança e Confiabilidade</h3>

                <p className="text-foreground/70">
                  Sistema seguro, online e preparado para o crescimento da sua clínica.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-10 bg-linear-to-br from-(--sisce-cyan) via-(--sisce-pink) to-(--sisce-purple)">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl text-white mb-6">Pronto para Transformar seu Negócio?</h2>
            <p className="text-xl text-white/90 mb-8">
              Comece hoje mesmo a usar o sistema mais completo para gestão de estética
            </p>
            <div className="flex gap-4 justify-center">
              <Link
                href="/contato"
                className="bg-white text-(--sisce-navy) px-8 py-4 rounded-lg hover:bg-white/90 transition-colors"
              >
                Solicitar Demonstração
              </Link>
              <Link
                href="/planos"
                className="border-2 border-white text-white px-8 py-4 rounded-lg hover:bg-white/10 transition-colors"
              >
                Ver Planos e Preços
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
